import React, { useState, useEffect } from "react";
import { useSelector, useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import SocialAccountModal from "./SocialAccountModal";
import {
  getNotificationPreferencesAPI,
  updateNotificationPreferencesAPI,
  updateCourseNotifications,
  updateInAppNotifications,
  updateSmsNotifications,
  updateEmailNotifications,
  hideConfirmationModal,
  clearMessage,
} from "../../redux/features/notificationPreferencesSlice";
import {
  getSocialAccountsAPI,
  unlinkSocialAccountAPI,
  showUnlinkConfirmation,
  hideConfirmationModal as hideSocialConfirmationModal,
  clearMessage as clearSocialMessage,
} from "../../redux/features/socialAccountsSlice";

const SettingComponent = ({ userRole }) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const auth = useSelector((state) => state.auth);
  const { preferences, isLoading, showConfirmationModal, isError, message } =
    useSelector((state) => state.notificationPreferences);

  const {
    socialAccounts,
    isLoading: isLoadingSocial,
    isUnlinking,
    error: socialError,
    message: socialMessage,
    showConfirmationModal: showSocialConfirmationModal,
    confirmationData,
  } = useSelector((state) => state.socialAccounts);

  const currentRole = userRole || auth?.role?.toLowerCase() || 'student';

  // Local state for form inputs - all false by default (first time)
  const [courseNotifications, setCourseNotifications] = useState({
    receiveNotifications: false,
    getReminder: false,
    stayUpdated: false,
  });
  const [inApp, setInApp] = useState(false);
  const [sms, setSms] = useState(false);
  const [email, setEmail] = useState(false);

  // Social Accounts State
  const [showSocialModal, setShowSocialModal] = useState(false);
  const [selectedProvider, setSelectedProvider] = useState("");

  // Fetch data on component mount
  useEffect(() => {
    dispatch(getSocialAccountsAPI());
    dispatch(getNotificationPreferencesAPI());
  }, [dispatch]);

  // Update local state when Redux state changes
  useEffect(() => {
    if (preferences) {
      // If preferences exist, use saved values
      setCourseNotifications(preferences.course_notifications || {
        receiveNotifications: false,
        getReminder: false,
        stayUpdated: false,
      });
      setInApp(preferences.in_app_notifications ?? false);
      setSms(preferences.sms_notifications ?? false);
      setEmail(preferences.email_notifications ?? false);
    } else {
      // If no preferences exist (first time), keep all false
      setCourseNotifications({
        receiveNotifications: false,
        getReminder: false,
        stayUpdated: false,
      });
      setInApp(false);
      setSms(false);
      setEmail(false);
    }
  }, [preferences]);

  // Clear messages when component unmounts
  useEffect(() => {
    return () => {
      dispatch(clearMessage());
      dispatch(clearSocialMessage());
    };
  }, [dispatch]);

  // Remove fetchSocialAccounts function as it's now handled by Redux

  const handleLinkAccount = (provider) => {
    // Modal functionality temporarily disabled - will be implemented later
    // setSelectedProvider(provider);
    // setShowSocialModal(true);
  };

  const handleUnlinkAccount = (account) => {
    dispatch(showUnlinkConfirmation(account));
  };

  const handleConfirmUnlink = () => {
    if (confirmationData) {
      dispatch(unlinkSocialAccountAPI(confirmationData.id));
    }
  };

  const handleSocialAccountSuccess = () => {
    dispatch(getSocialAccountsAPI()); // Refresh the list
  };

  const isProviderLinked = (provider) => {
    return socialAccounts.some((account) => account.provider === provider);
  };

  // Handle save button click
  const handleSaveClick = () => {
    const payload = {
      course_notifications: courseNotifications,
      in_app_notifications: inApp,
      sms_notifications: sms,
      email_notifications: email,
      notification_types: preferences?.notification_types || {},
    };

    console.log("Settings component - sending payload:", payload);
    console.log("courseNotifications:", courseNotifications);
    console.log("Type of courseNotifications:", typeof courseNotifications);

    dispatch(updateNotificationPreferencesAPI(payload));
  };

  // Close confirmation modal
  const closeConfirmationModal = () => {
    dispatch(hideConfirmationModal());
  };

  // Handle course notification changes
  const handleCourseNotificationChange = (key, value) => {
    const updated = { ...courseNotifications, [key]: value };
    setCourseNotifications(updated);
    dispatch(updateCourseNotifications(updated));
  };

  // Handle general notification changes
  const handleInAppChange = (value) => {
    setInApp(value);
    dispatch(updateInAppNotifications(value));
  };

  const handleSmsChange = (value) => {
    setSms(value);
    dispatch(updateSmsNotifications(value));
  };

  const handleEmailChange = (value) => {
    setEmail(value);
    dispatch(updateEmailNotifications(value));
  };

  return (
    <>
      {/* Error Messages */}
      {isError && message && (
        <div
          className="alert alert-danger alert-dismissible fade show"
          role="alert"
        >
          {message}
          <button
            type="button"
            className="btn-close"
            onClick={() => dispatch(clearMessage())}
          ></button>
        </div>
      )}

      {socialError && (
        <div
          className="alert alert-danger alert-dismissible fade show"
          role="alert"
        >
          {socialError}
          <button
            type="button"
            className="btn-close"
            onClick={() => dispatch(clearSocialMessage())}
          ></button>
        </div>
      )}

      {socialMessage && (
        <div
          className="alert alert-success alert-dismissible fade show"
          role="alert"
        >
          {socialMessage}
          <button
            type="button"
            className="btn-close"
            onClick={() => dispatch(clearSocialMessage())}
          ></button>
        </div>
      )}

      <div className="settings">
        <div className="card-default-style px-10 px-sm-20 px-md-30 px-lg-42 py-10 py-sm-20 py-md-35 py-lg-50 mb-10 mb-sm-20 mb-md-35 mb-lg-50">
          <h3 className="ah-heading-04 fw-bold text-black-100 mb-40">
            Notifications
          </h3>
          <h4 className="ah-heading-05 fw-bold text-black-100 mb-25">
            Course Notifications
          </h4>
          <p className="text-base fw-normal mb-32">
            Control which notifications you want to receive via email. Select
            your preferences below:
          </p>

          <div className="d-flex align-items-start mb-20">
            <input
              type="checkbox"
              id="receive-notifications"
              className="custom-checkbox me-19 flex-shrink-0"
              name="receive-notifications"
              checked={courseNotifications.receiveNotifications}
              onChange={(e) =>
                handleCourseNotificationChange(
                  "receiveNotifications",
                  e.target.checked,
                )
              }
            />
            <label
              htmlFor="receive-notifications"
              className="text-base fw-normal"
            >
              Receive notifications for new course materials, upcoming
              deadlines, and any important announcements from instructors or the
              LMS platform.
            </label>
          </div>

          <div className="d-flex align-items-start mb-20">
            <input
              type="checkbox"
              id="get-reminder"
              className="custom-checkbox me-19 flex-shrink-0"
              name="get-reminder"
              checked={courseNotifications.getReminder}
              onChange={(e) =>
                handleCourseNotificationChange("getReminder", e.target.checked)
              }
            />
            <label htmlFor="get-reminder" className="text-base fw-normal">
              Get reminders and updates about upcoming assignments, quizzes, and
              exam deadlines for courses you're enrolled in.
            </label>
          </div>

          <div className="d-flex align-items-start mb-20">
            <input
              type="checkbox"
              id="stay-updated"
              className="custom-checkbox me-19 flex-shrink-0"
              name="stay-updated"
              checked={courseNotifications.stayUpdated}
              onChange={(e) =>
                handleCourseNotificationChange("stayUpdated", e.target.checked)
              }
            />
            <label htmlFor="stay-updated" className="text-base fw-normal">
              Stay updated on new posts in course discussion forums, replies to
              your contributions, and activity in group projects.
            </label>
          </div>

          <hr className="my-40" />

          <div className="mb-40">
            <div className="form-check form-switch d-flex justify-content-between align-items-center ps-0 mb-17">
              <h3 className="text-black-100 ah-heading-05 fw-bold">
                In App Notifications
              </h3>
              <input
                className="form-check-input"
                type="checkbox"
                role="switch"
                checked={inApp}
                onChange={(e) => handleInAppChange(e.target.checked)}
              />
            </div>
            <p className="text-base fw-normal m-0">
              Manage your notification preferences within the app. Choose
              whether to receive all in-app alerts and push notifications.
            </p>
          </div>

          <div className="mb-40">
            <div className="form-check form-switch d-flex justify-content-between align-items-center ps-0 mb-17">
              <h3 className="text-black-100 ah-heading-05 fw-bold">
                SMS Notifications
              </h3>
              <input
                className="form-check-input"
                type="checkbox"
                role="switch"
                checked={sms}
                onChange={(e) => handleSmsChange(e.target.checked)}
              />
            </div>
            <p className="text-base fw-normal m-0">
              Enable/disable text message alerts.
            </p>
          </div>

          <div className="mb-40">
            <div className="form-check form-switch d-flex justify-content-between align-items-center ps-0 mb-17">
              <h3 className="text-black-100 ah-heading-05 fw-bold">
                Email Notifications
              </h3>
              <input
                className="form-check-input"
                type="checkbox"
                role="switch"
                checked={email}
                onChange={(e) => handleEmailChange(e.target.checked)}
              />
            </div>
            <p className="text-base fw-normal m-0">
              Set your email preferences for receiving updates and alerts
              directly to your inbox.
            </p>
          </div>
          <div className="text-end">
            <button
              type="button"
              className="btn btn-primary py-10 py-sm-14 py-md-10 px-20 px-sm-25 px-md-35 min-w-auto no-shadow"
              onClick={handleSaveClick}
              disabled={isLoading}
            >
              {isLoading ? "Saving..." : "Save"}
            </button>
          </div>
        </div>
        {/* Hide subscription management section for admin users */}
        {currentRole === "student" && (
          <div className="card-default-style px-10 px-sm-20 px-md-30 px-lg-42 py-10 py-sm-20 py-md-35 py-lg-50 mb-10 mb-sm-20 mb-md-35 mb-lg-50">
            <div className="d-flex align-items-end align-items-md-center justify-content-between flex-column flex-md-row">
              <div>
                <h3 className="ah-heading-04 fw-bold text-black-100 mb-40">
                  Subscription Management
                </h3>
                <p className="text-base fw-normal mb-32">
                  Review and manage your active subscriptions. You can upgrade,
                  downgrade, or cancel any plans currently in use.
                </p>
              </div>
              <div className="ms-0 ms-md-30">
                <button
                  className="btn btn-primary py-10 py-sm-14 py-md-17 px-20 px-sm-25 white-space-nowrap no-shadow"
                  type="button"
                  onClick={() => navigate("/manage-subscription")}
                >
                  Manage Subscriptions
                </button>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Instructor-specific Settings */}
      {currentRole === "instructor" && (
        <>
           {/* <div className="card-default-style px-10 px-sm-20 px-md-30 px-lg-42 py-10 py-sm-20 py-md-35 py-lg-50 mb-10 mb-sm-20 mb-md-35 mb-lg-50">
            <h3 className="ah-heading-04 fw-bold text-black-100 mb-40">
              Teaching Preferences
            </h3>
            <div className="d-flex align-items-end align-items-md-center justify-content-between flex-column flex-md-row mb-30">
              <div>
                <h4 className="ah-heading-05 fw-bold text-black-100 mb-15">
                  Course Management
                </h4>
                <p className="text-base fw-normal mb-0">
                  Set default course settings and teaching preferences.
                </p>
              </div>
              <div className="ms-0 ms-md-30">
                <button
                  className="btn btn-primary py-10 py-sm-14 py-md-17 px-20 px-sm-25 white-space-nowrap no-shadow"
                  type="button"
                >
                  Course Settings
                </button>
              </div>
            </div>
            <div className="d-flex align-items-end align-items-md-center justify-content-between flex-column flex-md-row mb-30">
              <div>
                <h4 className="ah-heading-05 fw-bold text-black-100 mb-15">
                  Student Communication
                </h4>
                <p className="text-base fw-normal mb-0">
                  Configure how you communicate with students and manage
                  announcements.
                </p>
              </div>
              <div className="ms-0 ms-md-30">
                <button
                  className="btn btn-outline-primary py-10 py-sm-14 py-md-17 px-20 px-sm-25 px-md-30 white-space-nowrap no-shadow"
                  type="button"
                >
                  Communication Settings
                </button>
              </div>
            </div>
            <div className="d-flex align-items-end align-items-md-center justify-content-between flex-column flex-md-row">
              <div>
                <h4 className="ah-heading-05 fw-bold text-black-100 mb-15">
                  Assessment Tools
                </h4>
                <p className="text-base fw-normal mb-0">
                  Configure grading rubrics and assessment preferences.
                </p>
              </div>
              <div className="ms-0 ms-md-30">
                <button
                  className="btn btn-outline-primary py-10 py-sm-14 py-md-17 px-20 px-sm-25 px-md-30 white-space-nowrap no-shadow"
                  type="button"
                >
                  Assessment Settings
                </button>
              </div>
            </div>
          </div> */}

          {/* <div className="card-default-style px-10 px-sm-20 px-md-30 px-lg-42 py-10 py-sm-20 py-md-35 py-lg-50 mb-10 mb-sm-20 mb-md-35 mb-lg-50">
            <h3 className="ah-heading-04 fw-bold text-black-100 mb-40">
              Professional Development
            </h3>
            <div className="d-flex align-items-end align-items-md-center justify-content-between flex-column flex-md-row">
              <div>
                <h4 className="ah-heading-05 fw-bold text-black-100 mb-15">
                  Teaching Resources
                </h4>
                <p className="text-base fw-normal mb-0">
                  Access teaching materials, templates, and professional
                  development courses.
                </p>
              </div>
              <div className="ms-0 ms-md-30">
                <button
                  className="btn btn-primary py-10 py-sm-14 py-md-17 px-20 px-sm-25 white-space-nowrap no-shadow"
                  type="button"
                >
                  Access Resources
                </button>
              </div>
            </div>
          </div> */}
        </>
      )}

      {/* Student-specific Settings */}
      {/* {currentRole === "student" && (
        <>
          <div className="card-default-style px-10 px-sm-20 px-md-30 px-lg-42 py-10 py-sm-20 py-md-35 py-lg-50 mb-10 mb-sm-20 mb-md-35 mb-lg-50">
            <h3 className="ah-heading-04 fw-bold text-black-100 mb-40">
              Learning Preferences
            </h3>
            <div className="d-flex align-items-end align-items-md-center justify-content-between flex-column flex-md-row mb-30">
              <div>
                <h4 className="ah-heading-05 fw-bold text-black-100 mb-15">
                  Study Schedule
                </h4>
                <p className="text-base fw-normal mb-0">
                  Set your preferred study times and learning schedule.
                </p>
              </div>
              <div className="ms-0 ms-md-30">
                <button
                  className="btn btn-primary py-10 py-sm-14 py-md-17 px-20 px-sm-25 white-space-nowrap no-shadow"
                  type="button"
                >
                  Set Schedule
                </button>
              </div>
            </div>
            <div className="d-flex align-items-end align-items-md-center justify-content-between flex-column flex-md-row mb-30">
              <div>
                <h4 className="ah-heading-05 fw-bold text-black-100 mb-15">
                  Learning Goals
                </h4>
                <p className="text-base fw-normal mb-0">
                  Set and track your learning objectives and career goals.
                </p>
              </div>
              <div className="ms-0 ms-md-30">
                <button
                  className="btn btn-outline-primary py-10 py-sm-14 py-md-17 px-20 px-sm-25 px-md-30 white-space-nowrap no-shadow"
                  type="button"
                >
                  Set Goals
                </button>
              </div>
            </div>
            <div className="d-flex align-items-end align-items-md-center justify-content-between flex-column flex-md-row">
              <div>
                <h4 className="ah-heading-05 fw-bold text-black-100 mb-15">
                  Progress Tracking
                </h4>
                <p className="text-base fw-normal mb-0">
                  Configure how you want to track your learning progress and
                  achievements.
                </p>
              </div>
              <div className="ms-0 ms-md-30">
                <button
                  className="btn btn-outline-primary py-10 py-sm-14 py-md-17 px-20 px-sm-25 px-md-30 white-space-nowrap no-shadow"
                  type="button"
                >
                  Track Progress
                </button>
              </div>
            </div>
          </div>

          <div className="card-default-style px-10 px-sm-20 px-md-30 px-lg-42 py-10 py-sm-20 py-md-35 py-lg-50 mb-10 mb-sm-20 mb-md-35 mb-lg-50">
            <h3 className="ah-heading-04 fw-bold text-black-100 mb-40">
              Social Learning
            </h3>
            <div className="d-flex align-items-end align-items-md-center justify-content-between flex-column flex-md-row">
              <div>
                <h4 className="ah-heading-05 fw-bold text-black-100 mb-15">
                  Study Groups
                </h4>
                <p className="text-base fw-normal mb-0">
                  Join or create study groups with other students in your
                  courses.
                </p>
              </div>
              <div className="ms-0 ms-md-30">
                <button
                  className="btn btn-primary py-10 py-sm-14 py-md-17 px-20 px-sm-25 white-space-nowrap no-shadow"
                type="button"
                >
                  Join Study Groups
                </button>
              </div>
            </div>
          </div>
        </>
      )} */}

      {/* Social Account Modal */}
      <SocialAccountModal
        isOpen={showSocialModal}
        onClose={() => {
          setShowSocialModal(false);
          setSelectedProvider("");
        }}
        provider={selectedProvider}
        onSuccess={handleSocialAccountSuccess}
      />

      {/* Notification Preferences Confirmation Modal */}
      {showConfirmationModal && (
        <div
          className="modal fade show d-block"
          style={{ backgroundColor: "rgba(0,0,0,0.5)" }}
        >
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">Settings Saved Successfully</h5>
                <button
                  type="button"
                  className="btn-close"
                  onClick={closeConfirmationModal}
                ></button>
              </div>
              <div className="modal-body">
                <div className="text-center">
                  <i
                    className="bi bi-check-circle-fill text-success"
                    style={{ fontSize: "3rem" }}
                  ></i>
                  <p className="mt-3 mb-0">
                    Your notification preferences have been saved successfully.
                  </p>
                </div>
              </div>
              <div className="modal-footer">
                <button
                  type="button"
                  className="btn btn-primary"
                  onClick={closeConfirmationModal}
                >
                  OK
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Social Account Unlink Confirmation Modal */}
      {showSocialConfirmationModal && confirmationData && (
        <div
          className="modal fade show d-block"
          style={{ backgroundColor: "rgba(0,0,0,0.5)" }}
        >
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">Unlink Social Account</h5>
                <button
                  type="button"
                  className="btn-close"
                  onClick={() => dispatch(hideSocialConfirmationModal())}
                ></button>
              </div>
              <div className="modal-body">
                <div className="text-center">
                  <i
                    className="bi bi-exclamation-triangle-fill text-warning"
                    style={{ fontSize: "3rem" }}
                  ></i>
                  <p className="mt-3 mb-0">
                    Are you sure you want to unlink your{" "}
                    {confirmationData.provider} account? This action cannot be
                    undone.
                  </p>
                </div>
              </div>
              <div className="modal-footer">
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => dispatch(hideSocialConfirmationModal())}
                  disabled={isUnlinking}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="btn btn-danger"
                  onClick={handleConfirmUnlink}
                  disabled={isUnlinking}
                >
                  {isUnlinking ? "Unlinking..." : "Unlink Account"}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default SettingComponent;
